import type { ReactNode } from "react";
import type { Icon } from "@phosphor-icons/react";
import { BooksIcon, FlowArrowIcon, LightningIcon, RobotIcon, TimerIcon } from "@phosphor-icons/react";
import { InlineMarkdown } from "~/components/inline-markdown";
import {
  HighlightedTitle,
  OpenAffordance,
  PalettePill,
  PaletteRow,
  PaletteSection,
  PreviewByline,
  PreviewEyebrow,
  PreviewTitle,
  RowMeta,
} from "~/components/command-palette-parts";
import { formatSchedule, isReflectCron, type CronJob } from "~/hooks/queries/use-crons";
import type { WorkflowSchedule } from "~/hooks/queries/use-workflow-schedules";
import { formatWorkflowSchedule } from "~/features/crons/workflow-schedule-list";
import { formatDateTime } from "~/lib/local-datetime";
import { matchesSearchQuery } from "~/lib/search-match";

/**
 * FRONT-2436 — the five "build" kinds the command palette finds alongside its
 * content search: knowledge bases, agents, scheduled jobs (including timed
 * workflows), skills and workflows.
 *
 * Unlike the palette's other sections these are NOT searched server-side: each
 * is a list the person already has (the same read its own page makes), loaded
 * once while the palette is searching and filtered here with the palette's own
 * operators (`matchesSearchQuery`). Everything that decides WHAT shows and
 * WHERE Enter goes is a pure function in this file, so it is unit-testable
 * without a DOM; `command-palette.tsx` only renders it.
 */

export type BuildKind = "kb" | "agent" | "cron" | "skill" | "workflow";

/** One palette row. `value` is the cmdk value and must be unique across every
 *  section, hence the per-kind prefixes in `VALUE_PREFIX`. */
export interface BuildItem {
  kind: BuildKind;
  value: string;
  title: string;
  /** Matched against the query together with the title; shown in the preview. */
  description: string;
  /** The row's second line, when it should differ from the description. */
  subtitle?: string;
  /** One fact already on the list payload, shown as a pill in the preview. */
  fact?: { label?: string; value: string };
  /** A workflow listed under Scheduled jobs because it runs on a timer. */
  timedWorkflow?: boolean;
  /** Where Enter goes, a path inside the app. */
  to: string;
}

/** At most this many rows per build section (spec decision 2): five sections
 *  add at most fifteen rows to a palette whose other sections show five. */
export const BUILD_SECTION_CAP = 3;

export const BUILD_SECTION_LABEL: Record<BuildKind, string> = {
  kb: "Knowledge bases",
  agent: "Agents",
  cron: "Scheduled jobs",
  skill: "Skills",
  workflow: "Workflows",
};

/** The singular, for the preview eyebrow and the "Open …" affordance. */
const BUILD_KIND_NOUN: Record<BuildKind, string> = {
  kb: "Knowledge base",
  agent: "Agent",
  cron: "Scheduled job",
  skill: "Skill",
  workflow: "Workflow",
};

/** Same icons the sidebar uses for these destinations (`sidebar-nav-catalog`),
 *  and the Knowledge bases tab's own icon. */
export const BUILD_KIND_ICON: Record<BuildKind, Icon> = {
  kb: BooksIcon,
  agent: RobotIcon,
  cron: TimerIcon,
  skill: LightningIcon,
  workflow: FlowArrowIcon,
};

/**
 * Render order, mirroring the sidebar's grouping (spec decision 1): knowledge
 * bases sit with Files (both live on the Knowledge page), ahead of Apps; the
 * four Build kinds come after Apps, in the sidebar's own Build order.
 */
export const BUILD_KINDS_BEFORE_APPS: readonly BuildKind[] = ["kb"];
export const BUILD_KINDS_AFTER_APPS: readonly BuildKind[] = ["agent", "cron", "skill", "workflow"];

const VALUE_PREFIX = {
  kb: "kb-",
  agent: "agent-",
  cron: "cron-",
  timedWorkflow: "wfsched-",
  skill: "skill-",
  workflow: "workflow-",
} as const;

/** True when a palette value belongs to one of the build kinds. */
export function isBuildItemValue(value: string): boolean {
  return Object.values(VALUE_PREFIX).some((p) => value.startsWith(p));
}

// ---------------------------------------------------------------------------
// Sources → items
// ---------------------------------------------------------------------------

export interface BuildSources {
  knowledgeBases: ReadonlyArray<{ id: string; name: string; description: string | null; sharedWithYou?: boolean }>;
  agents: ReadonlyArray<{ id: string; name: string; description: string; origin: string }>;
  crons: ReadonlyArray<Pick<CronJob, "id" | "name" | "description" | "schedule" | "nextRunAt" | "system" | "task">>;
  workflowSchedules: ReadonlyArray<WorkflowSchedule>;
  skills: ReadonlyArray<{ slug: string; name: string; description: string }>;
  workflows: ReadonlyArray<{ id: string; title: string; description?: string | null; status: string }>;
}

const AGENT_ORIGIN_LABEL: Record<string, string> = {
  predefined: "Predefined",
  mine: "Mine",
  shared: "Shared",
  private: "Org private",
};

/** Agents page filtered to one agent: the "All" tab, so a remembered tab can't hide it. */
export function agentDestination(name: string): string {
  return `/agents?tab=all&q=${encodeURIComponent(name)}`;
}

export function skillDestination(slug: string): string {
  return `/skills?skill=${encodeURIComponent(slug)}`;
}

export function knowledgeBaseDestination(id: string): string {
  return `/files?tab=bases&kb=${encodeURIComponent(id)}`;
}

export function cronDestination(id: string): string {
  return `/crons/${encodeURIComponent(id)}`;
}

export function workflowDestination(id: string): string {
  return `/workflows/${encodeURIComponent(id)}`;
}

/**
 * Every source list as palette items, per kind, unfiltered. Reflection jobs are
 * left out: the Schedule page shows them as one "Reflection" row, not as jobs
 * someone made, and an archived workflow is not on the Workflows list.
 */
export function toBuildItems(sources: BuildSources): Record<BuildKind, BuildItem[]> {
  const kb = sources.knowledgeBases.map(
    (k): BuildItem => ({
      kind: "kb",
      value: `${VALUE_PREFIX.kb}${k.id}`,
      title: k.name,
      description: k.description ?? "",
      fact: k.sharedWithYou ? { value: "Shared with you" } : undefined,
      to: knowledgeBaseDestination(k.id),
    }),
  );
  const agent = sources.agents.map(
    (a): BuildItem => ({
      kind: "agent",
      value: `${VALUE_PREFIX.agent}${a.id}`,
      title: a.name,
      description: a.description,
      fact: { value: AGENT_ORIGIN_LABEL[a.origin] ?? a.origin },
      to: agentDestination(a.name),
    }),
  );
  const cronJobs = sources.crons
    .filter((c) => !isReflectCron(c))
    .map((c): BuildItem => {
      const schedule = formatSchedule(c.schedule);
      const next = c.nextRunAt ? formatDateTime(c.nextRunAt) : "";
      return {
        kind: "cron",
        value: `${VALUE_PREFIX.cron}${c.id}`,
        title: c.name,
        description: c.description ?? "",
        subtitle: schedule,
        fact: next ? { label: "Next run", value: next } : { label: "Schedule", value: schedule },
        to: cronDestination(c.id),
      };
    });
  const timed = sources.workflowSchedules.map((s): BuildItem => {
    const schedule = formatWorkflowSchedule(s);
    return {
      kind: "cron",
      value: `${VALUE_PREFIX.timedWorkflow}${s.triggerId}`,
      title: s.workflowTitle,
      description: "",
      subtitle: schedule,
      fact: { label: "Schedule", value: schedule },
      timedWorkflow: true,
      to: workflowDestination(s.workflowId),
    };
  });
  const skill = sources.skills.map(
    (s): BuildItem => ({
      kind: "skill",
      value: `${VALUE_PREFIX.skill}${s.slug}`,
      title: s.name || s.slug,
      description: s.description ?? "",
      to: skillDestination(s.slug),
    }),
  );
  const workflow = sources.workflows
    .filter((w) => w.status !== "archived")
    .map(
      (w): BuildItem => ({
        kind: "workflow",
        value: `${VALUE_PREFIX.workflow}${w.id}`,
        title: w.title || "Untitled workflow",
        description: w.description ?? "",
        fact: { value: w.status === "active" ? "Active" : "Draft" },
        to: workflowDestination(w.id),
      }),
    );
  return { kb, agent, cron: [...cronJobs, ...timed], skill, workflow };
}

/**
 * The rows each build section shows for `query`: matched on title and
 * description with the palette's operators, at most `BUILD_SECTION_CAP` each.
 * A query below the content-search minimum is the caller's to gate; an empty
 * one here would match everything.
 */
export function filterBuildItems(
  items: Record<BuildKind, BuildItem[]>,
  query: string,
): Record<BuildKind, BuildItem[]> {
  const pick = (list: BuildItem[]) =>
    list.filter((i) => matchesSearchQuery(`${i.title} ${i.description}`, query)).slice(0, BUILD_SECTION_CAP);
  return {
    kb: pick(items.kb),
    agent: pick(items.agent),
    cron: pick(items.cron),
    skill: pick(items.skill),
    workflow: pick(items.workflow),
  };
}

export const EMPTY_BUILD_ITEMS: Record<BuildKind, BuildItem[]> = {
  kb: [],
  agent: [],
  cron: [],
  skill: [],
  workflow: [],
};

/** Row values in render order for the given kinds — feeds `firstPaletteValue`. */
export function buildValues(hits: Record<BuildKind, BuildItem[]>, kinds: readonly BuildKind[]): string[] {
  return kinds.flatMap((k) => hits[k].map((i) => i.value));
}

/** The item behind a palette value, or undefined when it is not on screen. */
export function findBuildItem(hits: Record<BuildKind, BuildItem[]>, value: string): BuildItem | undefined {
  for (const k of [...BUILD_KINDS_BEFORE_APPS, ...BUILD_KINDS_AFTER_APPS]) {
    const hit = hits[k].find((i) => i.value === value);
    if (hit) return hit;
  }
  return undefined;
}

export function countBuildItems(hits: Record<BuildKind, BuildItem[]>): number {
  return hits.kb.length + hits.agent.length + hits.cron.length + hits.skill.length + hits.workflow.length;
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

/** A row's second line: its own subtitle, else its description. */
function rowSnippet(item: BuildItem): ReactNode {
  if (item.subtitle) return item.subtitle;
  if (item.description) return <InlineMarkdown as="span" text={item.description} />;
  return undefined;
}

export function BuildItemSection({
  kind,
  items,
  query,
  openItem,
}: {
  kind: BuildKind;
  items: BuildItem[];
  query: string;
  openItem: (item: BuildItem) => () => void;
}) {
  if (items.length === 0) return null;
  return (
    <PaletteSection label={BUILD_SECTION_LABEL[kind]} count={items.length} testId={`palette-section-${kind}`}>
      {items.map((item) => (
        <PaletteRow
          key={item.value}
          value={item.value}
          icon={item.timedWorkflow ? FlowArrowIcon : BUILD_KIND_ICON[kind]}
          title={<HighlightedTitle text={item.title} query={query} />}
          snippet={rowSnippet(item)}
          meta={item.timedWorkflow ? <RowMeta>Workflow</RowMeta> : undefined}
          onSelect={openItem(item)}
          testId={`palette-row-${item.value}`}
        />
      ))}
    </PaletteSection>
  );
}

/** The short preview card (spec decision 7): kind, name, description and the
 *  one fact the list already carries. No extra fetch. */
export function BuildItemPreview({ item, onOpen }: { item: BuildItem; onOpen?: () => void }) {
  const noun = BUILD_KIND_NOUN[item.kind];
  return (
    <div data-testid={`palette-preview-${item.kind}`}>
      <PreviewEyebrow label={item.timedWorkflow ? `${noun} · Workflow` : noun} />
      <PreviewTitle>{item.title}</PreviewTitle>
      {item.fact && (
        <PreviewByline>
          <PalettePill>
            {item.fact.label && <span className="font-normal opacity-70">{item.fact.label}</span>}
            <span className="tabular-nums">{item.fact.value}</span>
          </PalettePill>
        </PreviewByline>
      )}
      {item.description && (
        <InlineMarkdown className="mt-3 text-sm leading-relaxed text-muted-foreground" text={item.description} />
      )}
      <OpenAffordance label={item.timedWorkflow ? "Open workflow" : `Open ${noun.toLowerCase()}`} onOpen={onOpen} />
    </div>
  );
}
